import { Lock } from '@assets/svg';
import {
  DatePicker,
  Flex,
  Input,
  Steps,
  Text,
  TextToolTip,
  ChoiceInput,
  Dropdown,
} from '@components';
import { useUpdateUserInfoMutation } from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { investmentPersonalInformationFormSchema } from '@validationSchemas/investment';
import WithUser from '@HOCs/withUser';
import { noop } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer } from './PersonalInformation.styles';
import { PersonalInformationFormData } from './PersonalInformation.types';
import { WithUserProps } from 'HOCs/withUser';
import countriesJSON from '@assets/countries.json';
import { useMemo } from 'react';
import { getFlagEmoji } from '@utils/functions/getFlagFromCountryCode';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { InvestmentContainer } from '../Investment.styles';
import { TOTAL_STEPS } from '../Investment.consts';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';

const PersonalInformation: React.FC<WithUserProps> = ({ user }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [updateUserInfoMutation] = useUpdateUserInfoMutation();

  const pepDetailsBox = document.getElementById('pepDetails-box');

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<PersonalInformationFormData>({
    resolver: yupResolver(investmentPersonalInformationFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      ...user.basicDetails,
    },
    shouldUnregister: true,
  });
  const PEP = watch('pep');

  const countriesList = useMemo(() => {
    const countriesArray = countriesJSON as Array<any>;
    return (
      countriesArray.map((x) => ({
        name: x.name,
        value: x.name,
        icon: getFlagEmoji(x.code),
      })) ?? []
    );
  }, []);

  const onSubmit = async (data: PersonalInformationFormData) => {
    try {
      await updateUserInfoMutation({
        ...user,
        basicDetails: {
          ...user.basicDetails,
          ...data,
          pepDetails: data.pep ? data.pepDetails : undefined,
        },
      }).unwrap();

      navigate(`/investment-subscription/${productId}/contact-details`, {
        state: {
          progress: 3,
        },
        replace: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (!user) return <></>;

  if (user.personalDetailsLocked) {
    getPersonalDetailLockNavigation(navigate, productId)();
  }
  return (
    <InvestmentContainer data-testid='personal-information-container'>
      <ContentContainer>
        <Steps currentStep={3} totalSteps={TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.personalInformation.personalInformation' />
        </Text>
        <Flex mt={14} alignItems='center'>
          <Lock color={theme.palette.grey.A100} />
          <Text
            variant='h6'
            ml={5}
            fontWeight='fontWeightSemiBold'
            color={theme.palette.grey.A100}
          >
            <FormattedMessage id='investment.personalInformation.fullName' />
          </Text>
        </Flex>
        <Flex mt={6} gap={10}>
          <Input
            label={intl.formatMessage({
              id: 'signUp.form.firstName',
            })}
            inputProps={{ 'data-testid': 'first-name-input' }}
            fullWidth
            disabled
            value={user.basicDetails.firstName}
          />
          <Input
            label={intl.formatMessage({
              id: 'signUp.form.middleName',
            })}
            inputProps={{ 'data-testid': 'middle-name-input' }}
            fullWidth
            disabled
            value={user.basicDetails.middleName}
          />
          <Input
            label={intl.formatMessage({
              id: 'signUp.form.surname',
            })}
            inputProps={{ 'data-testid': 'surname-input' }}
            fullWidth
            disabled
            value={user.basicDetails.lastName}
          />
        </Flex>
        <Flex mt={16}>
          <DatePicker
            label={intl.formatMessage({ id: 'signUp.form.birthDate' })}
            value={user.basicDetails.dateOfBirth}
            onChange={noop}
            locked
          />
        </Flex>
        <Input
          label={intl.formatMessage({
            id: 'investment.personalInformation.cityOfBirth',
          })}
          inputProps={{ 'data-testid': 'city-of-birth-input' }}
          fullWidth
          sx={{ mt: 16 }}
          {...registerInput(register, 'cityOfBirth')}
          {...getErrorProps(intl, errors.cityOfBirth)}
        />
        <Box mt={16}>
          <Controller
            control={control}
            name='countryOfBirth'
            render={({ field }) => (
              <Dropdown
                label={intl.formatMessage({
                  id: 'investment.personalInformation.countryOfBirth',
                })}
                inputProps={{ 'data-testid': 'country-of-birth' }}
                menuItemData={countriesList}
                {...field}
              />
            )}
          />
        </Box>
        <Flex mt={15} mb={5}>
          <TextToolTip
            text={intl.formatMessage({
              id: 'investment.personalInformation.politicallyExposed',
            })}
            toolTipText={intl.formatMessage({
              id: 'investment.personalInformation.politicallyExposedTooltip',
            })}
          />
        </Flex>
        <Controller
          control={control}
          name='pep'
          render={({ field: { ref, value, ...restProps } }) => (
            <ChoiceInput
              direction='row'
              fullWidth
              choices={yesNoChoices}
              defaultValues={value !== undefined ? [value] : undefined}
              {...restProps}
              onChange={async (currentValue) => {
                await restProps.onChange(currentValue);
                if (currentValue && pepDetailsBox !== null) {
                  pepDetailsBox.scrollIntoView({
                    behavior: 'smooth',
                    block: currentValue ? 'start' : 'end',
                  });
                }
              }}
            />
          )}
        />
        <Box id='pepDetails-box'>
          <Input
            label={intl.formatMessage({
              id: 'investment.personalInformation.titleFunctionAndOrganization',
            })}
            inputProps={{ 'data-testid': 'pep-detail-input' }}
            sx={{ mt: 16, display: PEP ? 'flex' : 'none' }}
            fullWidth
            {...registerInput(register, 'pepDetails')}
            {...getErrorProps(intl, errors.pepDetails)}
          />
        </Box>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/progress`, {
              state: {
                progress: 2,
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(PersonalInformation);
